/**
 * Shared sabotage deploy flow (SAB-540 C3b). Target-select modal -> the existing
 * nfl-apply-sabotage engine -> deploy-success takeover. Used by the store arsenal
 * (NFLSabotagesPage) and the matchup-view Drop CTA (NFLMatchupDetail) so both
 * deploy identically. Does not touch the deploy engine, only calls it.
 */
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { trackEvent } from '@/lib/analytics';
import { Loader2, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ArtChip, Scrim, DAMAGE_GRADIENT } from './sabotageStoreUI';

export interface DeployContext {
  leagueId: string;
  matchupId: string;
  opponentName: string;
  week: number;
  inventoryId: string;
  card: { name: string; emoji: string | null; target_scope: string; rarity: string };
}

export default function SabotageDeployDialog({
  ctx, onClose, onDeployed,
}: {
  ctx: DeployContext | null;
  onClose: () => void;
  onDeployed?: () => void;
}) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!ctx) return null;
  const { card, opponentName, week } = ctx;
  const isLW = card.target_scope === 'league';

  const close = () => { setSuccess(false); onClose(); };

  const handleDeploy = async () => {
    setBusy(true);
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      if (!token) { toast({ title: 'Sign in to deploy', variant: 'destructive' }); return; }
      const res = await fetch(`${(supabase as any).supabaseUrl}/functions/v1/nfl-apply-sabotage`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'apply', league_id: ctx.leagueId, matchup_id: ctx.matchupId, inventory_id: ctx.inventoryId }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        const code: string = data.error_code || data.error || '';
        const soft = /WEEKLY_LIMIT_REACHED|LEAGUE_LEGENDARY_TAKEN/i.test(code);
        const msg =
          /WEEKLY_LIMIT_REACHED/i.test(code) ? "You've already played your one sabotage this week." :
          /LEAGUE_LEGENDARY_TAKEN/i.test(code) ? 'The league-wide Legendary slot is taken this week. Your card rolls over to next week.' :
          /CARD_EXPIRED/i.test(code) ? 'This card expired when its matchup locked.' :
          (data.error || 'Deploy failed. Please try again.');
        toast({ title: soft ? 'Not this week' : 'Deploy failed', description: msg, variant: soft ? 'default' : 'destructive' });
        if (soft) close();
        return;
      }
      trackEvent('sabotage_dropped', { rarity: card.rarity, target_scope: card.target_scope });
      setSuccess(true);
      onDeployed?.();
    } catch (err) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  // Success confirmation is a modal overlay (portaled, on top of the page) so it
  // never renders inline in a matchup card. The persistent "sabotages in play"
  // list stays on the matchup underneath; this is the transient "bomb dropped"
  // moment, dismissed with Done.
  if (success) {
    return (
      <Scrim onClose={close}>
        <div className="text-center py-1">
          <div className="mx-auto h-20 w-20 rounded-2xl flex items-center justify-center text-4xl border-2 animate-in zoom-in-50 duration-500" style={{ borderColor: 'hsl(0 84% 60% / 0.5)', background: 'hsl(0 84% 60% / 0.1)', boxShadow: '0 0 40px hsl(0 84% 60% / 0.35)' }}>💥</div>
          <p className="mt-4 text-xs font-display font-bold uppercase tracking-[0.1em] text-damage">Sabotage deployed</p>
          <h2 className="font-display font-extrabold text-[22px] text-foreground mt-1">{card.name} dropped</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {isLW ? 'Live on the whole league.' : <>Dropped on <b className="text-foreground">{opponentName}</b>.</>} Your whole league can see it.
          </p>
        </div>
        <button onClick={close} className="mt-5 w-full h-11 rounded-xl text-white font-display font-bold" style={{ background: DAMAGE_GRADIENT }}>Done</button>
      </Scrim>
    );
  }

  return (
    <Scrim onClose={close}>
      <div className="text-center">
        <h2 className="font-display font-extrabold text-xl text-foreground">Pick your target</h2>
        <p className="text-sm text-muted-foreground">Dropping <b className="text-foreground">{card.name}</b>, public and single-use.</p>
      </div>
      {isLW ? (
        <div className="mt-4 rounded-xl border p-4 text-center border-[hsl(280,85%,60%/0.45)] bg-[hsl(280,85%,60%/0.08)]">
          <div className="flex justify-center"><ArtChip emoji={card.emoji} r="legendary" size={56} /></div>
          <p className="font-display font-bold text-foreground mt-2">The entire league</p>
          <p className="text-xs text-muted-foreground">All teams feel this one, no single target to pick.</p>
        </div>
      ) : (
        <>
          <div className="mt-4 rounded-xl border border-damage bg-damage/[0.06] p-3 flex items-center gap-3">
            <span className="h-[50px] w-[50px] rounded-full bg-muted flex items-center justify-center font-display font-bold text-foreground">{(opponentName[0] || 'O').toUpperCase()}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground">{opponentName}</p>
              <p className="text-[11px] text-muted-foreground">your Week {week} opponent</p>
            </div>
            <span className="h-4 w-4 rounded-full border-2 border-damage bg-damage" />
          </div>
          <p className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground"><Lock className="h-3.5 w-3.5" /> Other teams locked, this sabotage only hits your matchup opponent.</p>
        </>
      )}
      <div className="mt-4 space-y-2">
        <button onClick={handleDeploy} disabled={busy} className="w-full h-11 rounded-xl text-white font-display font-bold shadow-[0_8px_24px_hsl(0,84%,50%/0.35)] disabled:opacity-60 flex items-center justify-center" style={{ background: DAMAGE_GRADIENT }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isLW ? 'Unleash on the league' : `Drop it on ${opponentName}`}
        </button>
        <button onClick={close} className="w-full text-sm text-muted-foreground hover:text-foreground py-1">Not yet</button>
      </div>
    </Scrim>
  );
}
